/**
 * Display-only quote evidence. Stored mail, stage inputs and outgoing content never use these marks.
 * Client delimiters nominate a tail; they do not prove that it is safe to hide. Every meaningful
 * word (including short answers/contact details) and every link/image reference must be present
 * together in one earlier loaded message. A mixed or uncertain tail stays visible in full. The
 * client's own header lines are in neither proof (their words, and their links, are the client's), and
 * a `mailto:` link that only repeats its shown address adds nothing to the words.
 */
import {
  HEADER_LINE,
  isAttribution,
  isOutlookHeader,
  ORIGINAL_MESSAGE,
} from "../../../skills/leadcue-reply/scripts/lib/quote-markers.ts";

/** Only loaded earlier HTML can establish duplication; plain text alone cannot prove media. */
export type QuoteMessage = { readonly id: string; readonly html: string };

export type EmailDisplayRegion =
  | { readonly kind: "visible"; readonly html: string }
  | {
      readonly kind: "duplicate_quote";
      /** Unmarked sanitized HTML, before spacing or display annotations. */
      readonly html: string;
      readonly representedBy: readonly [string, ...string[]];
    };

export type QuoteFold =
  | { kind: "none"; html: string }
  | { kind: "folded"; html: string; regions: readonly EmailDisplayRegion[] }
  | {
      kind: "shown";
      html: string;
      reason: "only_quote" | "inline_reply" | "unconfirmed" | "uncertain";
    };

const QUOTE_ATTRIBUTE = "data-lcr-quote";

const CONTAINER_CLASSES = new Set([
  "gmail_quote",
  "gmail_quote_container",
  "yahoo_quoted",
  "moz-cite-prefix",
]);
const BOUNDARY_IDS = new Set(["divRplyFwdMsg", "appendonsend"]);

const BLOCK_TAGS = new Set([
  "blockquote",
  "br",
  "caption",
  "center",
  "dd",
  "div",
  "dl",
  "dt",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "li",
  "ol",
  "p",
  "pre",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
]);

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

function isElement(node: Node | null): node is Element {
  return node?.nodeType === ELEMENT_NODE;
}

function isText(node: Node | null): node is Text {
  return node?.nodeType === TEXT_NODE;
}

function tagOf(element: Element): string {
  return element.localName.toLowerCase();
}

function classesOf(element: Element): string[] {
  return (element.getAttribute("class") ?? "").split(/\s+/).filter(Boolean);
}

function normalize(text: string): string {
  return text
    .replaceAll(" ", " ")
    .replaceAll(/[‘’]/g, "'")
    .replaceAll(/[“”]/g, '"')
    .replaceAll(/\s+/g, " ")
    .trim();
}

/** Text with a line break at every block boundary, as a reader would see the lines. */
function linesOf(nodes: Iterable<Node>): string[] {
  const parts: string[] = [];
  const walk = (node: Node) => {
    if (isText(node)) {
      parts.push(node.data);
      return;
    }
    if (!isElement(node)) return;
    const block = BLOCK_TAGS.has(tagOf(node));
    if (block) parts.push("\n");
    for (const child of node.childNodes) walk(child);
    if (block) parts.push("\n");
  };
  for (const node of nodes) walk(node);
  return parts
    .join("")
    .split("\n")
    .map((line) =>
      line
        .replaceAll(" ", " ")
        .replaceAll(/[ \t]+/g, " ")
        .trim(),
    )
    .filter((line) => line.length > 0);
}

function textOf(node: Node): string {
  return linesOf([node]).join("\n");
}

function hasText(nodes: Iterable<Node>): boolean {
  return linesOf(nodes).some((line) => /[\p{L}\p{N}]/u.test(line));
}

/** A block of plain-text quoting: every line it shows starts with `>`. */
function isAngleQuoted(node: Node): boolean {
  if (!isElement(node)) return false;
  const lines = linesOf([node]);
  return lines.length > 0 && lines.every((line) => line.startsWith(">"));
}

/**
 * A blockquote, Yahoo's quote container (which holds the original in plain `div`s), or plain-text
 * quoting: a `>` block, or a `>` line between line breaks.
 */
function isQuoteBlock(node: Node | null): boolean {
  if (!node) return false;
  if (isText(node)) return node.data.trim().startsWith(">");
  if (!isElement(node)) return false;
  return (
    tagOf(node) === "blockquote" || classesOf(node).includes("yahoo_quoted") || isAngleQuoted(node)
  );
}

function significant(node: Node): boolean {
  if (isText(node)) return node.data.trim().length > 0;
  if (!isElement(node)) return false;
  const tag = tagOf(node);
  return tag === "img" || tag === "hr" || hasText([node]);
}

/** The next sibling that shows something, climbing out of wrappers that hold nothing after `node`. */
function nextShown(node: Node, root: Node): Node | null {
  let current: Node | null = node;
  while (current && current !== root) {
    let next: Node | null = current.nextSibling;
    while (next && !significant(next)) next = next.nextSibling;
    if (next) return next;
    current = current.parentNode;
  }
  return null;
}

/** Widen a start to the outermost wrapper that contains nothing but it. */
function widen(node: Node, root: Node): Node {
  let current = node;
  const own = textOf(node);
  while (current.parentNode && current.parentNode !== root && textOf(current.parentNode) === own) {
    current = current.parentNode;
  }
  return current;
}

type Start = { node: Node; style: "blockquote" | "header" };

function findStart(parent: Node, root: Node): Start | null {
  for (const node of parent.childNodes) {
    if (isText(node)) {
      const line = node.data.trim();
      if (isAttribution(line) && isQuoteBlock(nextShown(node, root))) {
        return { node, style: "blockquote" };
      }
      continue;
    }
    if (!isElement(node)) continue;
    const tag = tagOf(node);
    if (classesOf(node).some((name) => CONTAINER_CLASSES.has(name))) {
      return { node, style: "blockquote" };
    }
    if (tag === "blockquote" && node.getAttribute("type") === "cite") {
      return { node, style: "blockquote" };
    }
    const id = node.getAttribute("id");
    if (id && BOUNDARY_IDS.has(id)) {
      // Outlook on the web draws a rule just before its header; it belongs to the quote.
      const previous = node.previousElementSibling;
      const rule = previous && tagOf(previous) === "hr" ? previous : null;
      return { node: rule ?? node, style: "header" };
    }
    const text = textOf(node);
    const firstLine = text.split("\n")[0] ?? "";
    if (ORIGINAL_MESSAGE.test(firstLine)) return { node: widen(node, root), style: "header" };
    if (isOutlookHeader(text)) {
      // Descend first: the smallest block holding the whole header is where the quote starts.
      return findStart(node, root) ?? { node: widen(node, root), style: "header" };
    }
    if (
      firstLine === text &&
      isAttribution(text) &&
      node.querySelector("blockquote") === null &&
      isQuoteBlock(nextShown(node, root))
    ) {
      return { node: widen(node, root), style: "blockquote" };
    }
    const inner = findStart(node, root);
    if (inner) return inner;
  }
  return null;
}

/** The start and everything after it in document order, ancestors excluded. */
function tailOf(start: Node, root: Node): Node[] {
  const tail: Node[] = [start];
  let current: Node = start;
  while (current !== root) {
    for (let next = current.nextSibling; next; next = next.nextSibling) tail.push(next);
    const parent = current.parentNode;
    if (!parent) break;
    current = parent;
  }
  return tail;
}

/** What the tail leaves visible: tail members dropped, their ancestors opened up. */
function outsideTail(parent: Node, tail: ReadonlySet<Node>): Node[] {
  const kept: Node[] = [];
  for (const node of parent.childNodes) {
    if (tail.has(node)) continue;
    if ([...tail].some((member) => node.contains(member))) kept.push(...outsideTail(node, tail));
    else kept.push(node);
  }
  return kept;
}

/** A quote block, a quote container, or an element holding one somewhere inside. */
function holdsQuote(node: Node): boolean {
  if (isQuoteBlock(node)) return true;
  if (!isElement(node)) return false;
  if (classesOf(node).some((name) => CONTAINER_CLASSES.has(name))) return true;
  return [...node.childNodes].some(holdsQuote);
}

/**
 * A blockquote-style quote ends with its last quote block, not at the end of the email: Gmail puts
 * the sender's signature after its quote container by default, and mail servers append disclaimers
 * there. What follows stays visible and is not an answer written into the quote (talon draws the
 * same line: it cuts `div.gmail_quote` and leaves its siblings). An Outlook-style header has no
 * element that closes the quoted message, so its quote still runs to the end.
 */
function quoteRegion(tail: readonly Node[]): readonly Node[] {
  const last = tail.findLastIndex(holdsQuote);
  return last < 0 ? tail : tail.slice(0, last + 1);
}

/** An answer typed between quoted blocks stays visible, even when its words appeared earlier. */
function writtenInsideQuote(tail: readonly Node[]): boolean {
  let quoteSeen = false;
  let written = false;
  const visit = (node: Node): void => {
    if (written) return;
    if (isQuoteBlock(node)) {
      quoteSeen = true;
      return;
    }
    if (isText(node)) {
      const line = node.data.trim();
      if (quoteSeen && /[\p{L}\p{N}]/u.test(line) && !isAttribution(line)) written = true;
      return;
    }
    if (!isElement(node) || isAttribution(textOf(node))) return;
    for (const child of node.childNodes) visit(child);
  };
  for (const node of tail) visit(node);
  return written;
}

/**
 * Gmail marks its attribution line with this class in every language, so its wording need not be
 * known: a Polish Gmail line starts with the weekday ("pon., 2 lis 2026 o 16:42 … napisał(a):"),
 * which no wording list here or in the libraries checked covers.
 */
function isGmailAttribution(node: Node): boolean {
  return isElement(node) && classesOf(node).includes("gmail_attr");
}

type ProofView = { readonly copy: HTMLElement; readonly headerPictures: readonly Element[] };

/**
 * A copy of the nodes without the mail client's own header lines: a complete header block, never
 * arbitrary lines beginning with "Subject:" or "To:", and an attribution line that introduces a quote.
 * A client writes those lines, not the lead, and links the people in them (`mailto:` on a From or
 * To line, an attribution's address), so neither their words nor their links are evidence of
 * anything the lead added. Pictures in them are not links and stay evidence: `headerPictures`.
 */
function withoutHeaderLines(nodes: readonly Node[], document: Document): ProofView {
  const copy = document.createElement("div");
  for (const node of nodes) copy.appendChild(node.cloneNode(true));
  const headerPictures: Element[] = [];
  const removeMetadata = (node: Node): void => {
    const lines = linesOf([node]);
    const text = lines.join("\n");
    if (
      (isOutlookHeader(text) && lines.every((line) => HEADER_LINE.test(line))) ||
      ((isAttribution(text) || isGmailAttribution(node)) && isQuoteBlock(nextShown(node, copy)))
    ) {
      if (isElement(node)) headerPictures.push(...node.querySelectorAll("img"));
      node.parentNode?.removeChild(node);
      return;
    }
    for (const child of [...node.childNodes]) removeMetadata(child);
  };
  for (const node of [...copy.childNodes]) removeMetadata(node);
  return { copy, headerPictures };
}

function evidenceText({ copy }: ProofView): string {
  return normalize(
    linesOf([copy])
      .map((line) => line.replace(/^(?:>\s?)+/, ""))
      .join(" "),
  );
}

/**
 * A `mailto:` link whose shown text is the very address it points to, with no tooltip: what a mail
 * client makes of a bare address while quoting. Its target says nothing its text does not, and the
 * words proof already requires that text in the earlier message. A target that differs from the
 * text (another address, a subject) or a title is something the lead may have added. `tel:` links
 * never get here: the sanitizer drops every href but http, https and mailto.
 */
function repeatsItsAddress(element: Element): boolean {
  if (element.hasAttribute("title")) return false;
  const address = `mailto:${normalize(element.textContent ?? "")}`.toLowerCase();
  return (element.getAttribute("href") ?? "").toLowerCase() === address;
}

/**
 * Exact reference and alternative text comparison. A source-less image or link has lost evidence.
 * Links in the client's header lines and links that only repeat their own address are not references.
 */
function mediaReferences({ copy, headerPictures }: ProofView): string[] | null {
  const references: string[] = [];
  for (const element of [...copy.querySelectorAll("img, a"), ...headerPictures]) {
    if (repeatsItsAddress(element)) continue;
    const image = tagOf(element) === "img";
    const source = image
      ? (element.getAttribute("src") ?? element.getAttribute("data-remote-src"))
      : element.getAttribute("href");
    if (!source) return null;
    references.push(
      JSON.stringify([
        tagOf(element),
        source,
        element.getAttribute("alt"),
        element.getAttribute("title"),
      ]),
    );
  }
  return references;
}

/** Multiplicity matters: a second reference is not proved by a single occurrence. */
function containsReferences(earlier: readonly string[], quoted: readonly string[]): boolean {
  const remaining = [...earlier];
  return quoted.every((reference) => {
    const index = remaining.indexOf(reference);
    if (index < 0) return false;
    remaining.splice(index, 1);
    return true;
  });
}

function representedBy(
  tail: readonly Node[],
  earlier: readonly QuoteMessage[],
  document: Document,
  parse: (html: string) => Document,
): string[] {
  const quote = withoutHeaderLines(tail, document);
  const text = evidenceText(quote);
  const media = mediaReferences(quote);
  if (!text || media === null) return [];
  return earlier
    .filter((message) => {
      const nodes = [...parse(message.html).body.childNodes];
      const previous = withoutHeaderLines(nodes, document);
      const previousText = evidenceText(previous);
      const previousMedia = mediaReferences(previous);
      return (
        ` ${previousText} `.includes(` ${text} `) &&
        previousMedia !== null &&
        containsReferences(previousMedia, media)
      );
    })
    .map((message) => message.id);
}

function regionHtml(nodes: readonly Node[], document: Document): string {
  const copy = document.createElement("div");
  for (const node of nodes) copy.appendChild(node.cloneNode(true));
  return copy.innerHTML;
}

/** An element that shows nothing: no text, picture, rule or table, such as a `<br>` or an empty `<div>`. */
function blank(node: Node): boolean {
  if (isText(node)) return node.data.trim() === "";
  return (
    isElement(node) &&
    !hasText([node]) &&
    tagOf(node) !== "img" &&
    tagOf(node) !== "hr" &&
    node.querySelector("img, hr, table") === null
  );
}

/**
 * The empty lines just above the quote, which mail clients write between the reply and the quote
 * (Gmail puts a `<br>` before its container). They belong with the quote: left visible, they stand
 * as a blank line under the author's last one. Walks back from the start through its siblings and
 * into the last children of the reply before it, stopping at the first thing that shows.
 */
function blankLinesAbove(start: Node, root: Node): Node[] {
  const found: Node[] = [];
  const intoTrailing = (node: Node): void => {
    for (let child = node.lastChild; child; child = child.previousSibling) {
      if (!blank(child)) {
        if (isElement(child)) intoTrailing(child);
        return;
      }
      found.push(child);
    }
  };
  for (
    let current: Node | null = start;
    current && current !== root;
    current = current.parentNode
  ) {
    for (let previous = current.previousSibling; previous; previous = previous.previousSibling) {
      if (!blank(previous)) {
        if (isElement(previous)) intoTrailing(previous);
        return found;
      }
      found.push(previous);
    }
  }
  return found;
}

function mark(tail: readonly Node[], start: Node, document: Document): void {
  for (const node of tail) {
    const value = node === start ? "start" : "";
    if (isElement(node)) {
      node.setAttribute(QUOTE_ATTRIBUTE, value);
    } else if (isText(node) && node.data.trim() && node.parentNode) {
      const wrapper = document.createElement("span");
      wrapper.setAttribute(QUOTE_ATTRIBUTE, value);
      node.parentNode.replaceChild(wrapper, node);
      wrapper.appendChild(node);
    }
  }
}

/** Callers supply only messages earlier than the current one in the loaded thread. */
export function foldQuotedHistory(
  sanitizedHtml: string,
  earlier: readonly QuoteMessage[],
  parse: (html: string) => Document,
): QuoteFold {
  try {
    return confirmedFold(sanitizedHtml, earlier, parse);
  } catch {
    // A display parser failure must never turn into hidden mail.
    return { kind: "shown", html: sanitizedHtml, reason: "uncertain" };
  }
}

function confirmedFold(
  sanitizedHtml: string,
  earlier: readonly QuoteMessage[],
  parse: (html: string) => Document,
): QuoteFold {
  const document = parse(sanitizedHtml);
  const root = document.body;
  const start = findStart(root, root);
  if (!start) return { kind: "none", html: sanitizedHtml };
  const tail = tailOf(start.node, root);
  // Nothing written above the quote: whatever sits below it is the answer, and the quote its context.
  if (!hasText(outsideTail(root, new Set(tail)))) {
    return { kind: "shown", html: sanitizedHtml, reason: "only_quote" };
  }
  const quote = start.style === "blockquote" ? quoteRegion(tail) : tail;
  if (start.style === "blockquote" && writtenInsideQuote(quote)) {
    return { kind: "shown", html: sanitizedHtml, reason: "inline_reply" };
  }
  // A forward's participants/subject are correspondence of their own, not just repeated body text.
  if (linesOf(tail).some((line) => ORIGINAL_MESSAGE.test(line))) {
    return { kind: "shown", html: sanitizedHtml, reason: "uncertain" };
  }
  const [first, ...rest] = representedBy(quote, earlier, document, parse);
  if (first === undefined) return { kind: "shown", html: sanitizedHtml, reason: "unconfirmed" };
  const hidden = [...blankLinesAbove(start.node, root).reverse(), ...quote];
  const original = regionHtml(hidden, document);
  mark(hidden, start.node, document);
  const visible = root.cloneNode(true);
  if (!isElement(visible)) return { kind: "shown", html: sanitizedHtml, reason: "uncertain" };
  for (const node of visible.querySelectorAll(`[${QUOTE_ATTRIBUTE}]`)) node.remove();
  return {
    kind: "folded",
    html: root.innerHTML,
    regions: [
      { kind: "visible", html: visible.innerHTML },
      { kind: "duplicate_quote", html: original, representedBy: [first, ...rest] },
    ],
  };
}
